/**
 * AIInputComposer
 *
 * Reusable prompt input with file attachment support.
 * Handles: text input, file picker, clipboard paste (images),
 * drag-and-drop, upload progress, and send blocking until uploads complete.
 *
 * Includes a generation mode selector (dropdown beside the send button):
 *  - "Generate" (default): skip analysis, go straight to generation
 *  - "Analyze first": run goal analysis with clarifying questions before generating
 */

import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BoltIcon from '@mui/icons-material/Bolt';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { colors } from '../theme/colors';
import { useFileUpload, ACCEPT_STRING } from '../hooks/useFileUpload';
import AttachmentPreview from './AttachmentPreview';

export type GenerationMode = 'generate' | 'analyze';

interface AIInputComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text: string, attachmentIds: string[], mode: GenerationMode) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** "search" renders a pill-shaped search bar with a leading search icon */
  variant?: 'default' | 'search';
  /** Hide the mode selector (e.g. when analysis isn't applicable) */
  hideMode?: boolean;
  /** Hide file attachment UI (e.g. for unauthenticated preview mode) */
  hideAttachments?: boolean;
}

const MODE_LABELS: Record<GenerationMode, string> = {
  generate: 'Generate',
  analyze: 'Analyze first',
};

const AIInputComposer: React.FC<AIInputComposerProps> = ({
  value,
  onChange,
  onSubmit,
  loading = false,
  disabled = false,
  placeholder = 'Type your message here...',
  variant = 'default',
  hideMode = false,
  hideAttachments = false,
}) => {
  const isSearch = variant === 'search';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dragCounterRef = useRef(0);
  const [mode, setMode] = useState<GenerationMode>('generate');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const {
    attachments,
    addFiles,
    removeAttachment,
    clearAll,
    hasUploading,
    hasFailed,
    readyTempUploadIds,
  } = useFileUpload();

  const showAttachments = !hideAttachments;

  const hasText = value.trim().length > 0;
  const hasReadyAttachments = showAttachments && readyTempUploadIds.length > 0;
  const canSend =
    !loading &&
    !disabled &&
    !(showAttachments && hasUploading) &&
    !(showAttachments && hasFailed) &&
    (hasText || hasReadyAttachments);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;
    onSubmit(
      value.trim(),
      showAttachments ? readyTempUploadIds : [],
      mode
    );
    onChange('');
    if (showAttachments) clearAll();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (!showAttachments) return;
    const items = e.clipboardData?.files;
    if (items && items.length > 0) {
      e.preventDefault();
      addFiles(items);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFiles(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ---- Drag and drop ----

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!showAttachments) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, [showAttachments]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!showAttachments) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, [showAttachments]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!showAttachments) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        addFiles(files);
      }
    },
    [addFiles, showAttachments]
  );

  const sendTooltip = hasUploading
    ? 'Waiting for uploads...'
    : hasFailed
      ? 'Remove failed files to send'
      : MODE_LABELS[mode];

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: isDragOver
          ? colors.blue[500]
          : isFocused
            ? colors.gray[400]
            : colors.gray[200],
        borderRadius: isSearch ? '28px' : '24px',
        overflow: 'hidden',
        position: 'relative',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
        bgcolor: isFocused ? '#fff' : colors.gray[50],
        boxShadow: isFocused
          ? '0 4px 16px rgba(0,0,0,0.10)'
          : '0 2px 8px rgba(0,0,0,0.04)',
        ...(isDragOver && {
          borderStyle: 'dashed',
          borderWidth: 2,
          bgcolor: colors.blue[50],
        }),
      }}
    >
      {/* Drag overlay */}
      {isDragOver && showAttachments && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(239, 246, 255, 0.92)',
            zIndex: 10,
            pointerEvents: 'none',
            borderRadius: '24px',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: colors.blue[500], mb: 1 }} />
          <Typography variant="body2" sx={{ color: colors.blue[700], fontWeight: 500 }}>
            Drop files to attach
          </Typography>
        </Box>
      )}

      {/* Attachment previews */}
      {showAttachments && attachments.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            px: 2.5,
            pt: 2,
            pb: 0,
          }}
        >
          {attachments.map((att) => (
            <AttachmentPreview
              key={att.id}
              attachment={att}
              onRemove={removeAttachment}
            />
          ))}
        </Box>
      )}

      {/* Text input area */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Hidden file input */}
        {showAttachments && (
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
            accept={ACCEPT_STRING}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {isSearch && (
            <SearchIcon sx={{ ml: 2.5, mt: 2.25, color: colors.gray[400], fontSize: 22, flexShrink: 0 }} />
          )}

          <TextField
            fullWidth
            multiline
            maxRows={8}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                px: 2.5,
                pt: 2,
                pb: 1,
                fontSize: '0.95rem',
                lineHeight: 1.6,
              },
            }}
          />
        </Box>

        {/* Bottom toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            pb: 1,
            pt: 0,
          }}
        >
          {/* Left: Attach button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {showAttachments && !isSearch && (
              <Tooltip title="Attach files">
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || loading}
                  size="small"
                  sx={{
                    color: colors.gray[400],
                    '&:hover': { color: colors.gray[600] },
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Right: Mode selector + Send button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Mode dropdown chip */}
            {!hideMode && (
              <>
                <Chip
                  label={MODE_LABELS[mode]}
                  size="small"
                  deleteIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                  onDelete={(e) => setMenuAnchor(e.currentTarget as HTMLElement)}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  disabled={loading || disabled}
                  sx={{
                    height: 26,
                    fontSize: 12,
                    fontWeight: 500,
                    bgcolor: colors.gray[100],
                    color: colors.gray[600],
                    '& .MuiChip-deleteIcon': {
                      color: colors.gray[400],
                      '&:hover': { color: colors.gray[600] },
                    },
                  }}
                />
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  slotProps={{ paper: { sx: { minWidth: 200, mt: -1 } } }}
                >
                  <MenuItem
                    selected={mode === 'generate'}
                    onClick={() => { setMode('generate'); setMenuAnchor(null); }}
                  >
                    <ListItemIcon>
                      <BoltIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Generate</ListItemText>
                    {mode === 'generate' && <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />}
                  </MenuItem>
                  <MenuItem
                    selected={mode === 'analyze'}
                    onClick={() => { setMode('analyze'); setMenuAnchor(null); }}
                  >
                    <ListItemIcon>
                      <QuestionAnswerOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Analyze first</ListItemText>
                    {mode === 'analyze' && <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />}
                  </MenuItem>
                </Menu>
              </>
            )}

            {/* Send button - filled circle */}
            <Tooltip title={sendTooltip}>
              <span>
                <IconButton
                  type="submit"
                  disabled={!canSend}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: canSend ? 'primary.main' : colors.gray[200],
                    color: canSend ? '#fff' : colors.gray[400],
                    '&:hover': {
                      bgcolor: canSend ? 'primary.dark' : colors.gray[200],
                    },
                    '&.Mui-disabled': {
                      bgcolor: colors.gray[200],
                      color: colors.gray[400],
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                  ) : (
                    <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AIInputComposer;
